import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import FeatherIcon from 'feather-icons-react';
import { Link } from 'react-router-dom';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { RootState, AppDispatch } from 'store';
import {
  deleteUser,
  editUser,
  UserType,
  UpdateUserPayload
} from 'store/userSlice';
import ActionDropdownItems from 'components/common/ActionDropdownItems';
import EditUserModal from 'components/modals/EditUserModal';
import PhoenixLoader from 'components/common/PhoenixLoader';
import DeleteUserModal from 'components/modals/DeleteUserModal';
import ViewUserModal from 'components/modals/ViewUserModal';
import { getRoleById } from 'constants/roles';
import { showSuccess, handleApiError } from 'utils/errorHandler';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faUserTag,
  faSitemap,
  faBuilding,
  faCheckCircle,
  faCog
} from '@fortawesome/free-solid-svg-icons';

interface UserTableProps {
  searchQuery?: string;
  selectedCompanyId?: number | null;
  selectedRole?: string | null;
  selectedStatus?: string | null;
}

const UserTable = ({
  searchQuery = '',
  selectedCompanyId,
  selectedRole = null,
  selectedStatus = null
}: UserTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, loading, error } = useSelector(
    (state: RootState) => state.users
  );

  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [openEditModal, setOpenEditModal] = useState(false);

  const [userToDelete, setUserToDelete] = useState<UserType | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [viewUser, setViewUser] = useState<UserType | null>(null);
  const [openViewModal, setOpenViewModal] = useState(false);

  // Note: fetchUsers is called by the parent component (user-list.tsx)
  // No need to fetch here to avoid duplicate API calls

  const filteredData = useMemo(() => {
    let filtered = data;

    // Filter by company if selectedCompanyId is provided
    if (selectedCompanyId !== null && selectedCompanyId !== undefined) {
      filtered = filtered.filter(user => user.company_id === selectedCompanyId);
    }

    // Filter by role
    if (selectedRole) {
      filtered = filtered.filter(
        user => user.role_id.toString() === selectedRole
      );
    }

    // Filter by status
    if (selectedStatus) {
      const isActive = selectedStatus === 'active';
      filtered = filtered.filter(user => user.is_active === isActive);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        user =>
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query) ||
          (user.department_name &&
            user.department_name.toLowerCase().includes(query)) ||
          (user.role_name && user.role_name.toLowerCase().includes(query)) ||
          (user.company_name && user.company_name.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [data, searchQuery, selectedCompanyId, selectedRole, selectedStatus]);

  const handleDeleteClick = (user: UserType) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (userToDelete) {
      try {
        await dispatch(deleteUser(userToDelete.id)).unwrap();
        setShowDeleteModal(false);
        showSuccess('User deleted successfully');
      } catch (error) {
        handleApiError(error, 'Failed to delete user');
        setShowDeleteModal(false);
      } finally {
        setUserToDelete(null);
      }
    }
  };

  const handleEdit = (user: UserType) => {
    setSelectedUser(user);
    setOpenEditModal(true);
  };

  const handleSaveEdit = async (
    userId: number,
    userData: UpdateUserPayload
  ) => {
    try {
      await dispatch(editUser({ id: userId, userData })).unwrap();
      setOpenEditModal(false);
      showSuccess('User updated successfully');
    } catch (error) {
      handleApiError(error, 'Failed to update user');
      setOpenEditModal(false);
    } finally {
      setSelectedUser(null);
    }
  };

  const handleView = (user: UserType) => {
    setViewUser(user);
    setOpenViewModal(true);
  };

  const table = useAdvanceTable({
    data: filteredData,
    columns: getColumns(handleView, handleEdit, handleDeleteClick),
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <AdvanceTableProvider {...table}>
      <div
        className="bg-white"
        style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}
      >
        {loading ? (
          <PhoenixLoader />
        ) : error ? (
          <p className="p-4 text-danger">Error: {error}</p>
        ) : (
          <>
            <div className="table-responsive" style={{ overflowY: 'visible' }}>
              <AdvanceTable
                tableProps={{
                  className: 'phoenix-table mb-0',
                  hover: true
                }}
                headerClassName=""
                rowClassName="hover-actions-trigger btn-reveal-trigger align-middle"
              />
            </div>
            <div className="table-footer-elegant">
              <AdvanceTableFooter pagination className="px-3 py-2" />
            </div>
          </>
        )}
      </div>

      {openEditModal && selectedUser && (
        <EditUserModal
          show={openEditModal}
          handleClose={() => {
            setOpenEditModal(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onSave={handleSaveEdit}
        />
      )}

      {showDeleteModal && userToDelete && (
        <DeleteUserModal
          show={showDeleteModal}
          userName={userToDelete.name}
          onClose={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
          onConfirm={confirmDelete}
        />
      )}

      {openViewModal && viewUser && (
        <ViewUserModal
          show={openViewModal}
          handleClose={() => {
            setOpenViewModal(false);
            setViewUser(null);
          }}
          user={viewUser}
        />
      )}
    </AdvanceTableProvider>
  );
};

export default UserTable;

function getColumns(
  onView: (user: UserType) => void,
  onEdit: (user: UserType) => void,
  onDelete: (user: UserType) => void
): ColumnDef<UserType>[] {
  return [
    {
      id: 'sn',
      header: 'SN',
      cell: ({ row }) => <span className="fw-semibold">{row.index + 1}</span>,
      meta: {
        headerProps: {
          style: { width: '80px' }
        },
        cellProps: {}
      }
    },
    {
      id: 'name',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
          Name
        </span>
      ),
      cell: ({ row: { original } }) => (
        <button
          className="btn btn-link p-0 fw-semibold text-decoration-none text-start"
          onClick={() => onView(original)}
        >
          {original.name}
        </button>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      accessorKey: 'email',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
          Email
        </span>
      ),
      cell: ({ row: { original } }) => (
        <Link className="text-body" to={`mailto:${original.email}`}>
          {original.email}
        </Link>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      id: 'role_name',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faUserTag} className="me-2 text-primary" />
          Role
        </span>
      ),
      cell: ({ row: { original } }) => {
        const role = getRoleById(original.role_id);
        return (
          <span className="text-body">
            {role
              ? role.name
              : original.role_name || `Role ${original.role_id}`}
          </span>
        );
      },
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      id: 'department_name',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faSitemap} className="me-2 text-primary" />
          Department
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className="text-body">
          {original.department_name === null ? '—' : original.department_name}
        </span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      id: 'company_name',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
          Company
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className="text-body">
          {original.company_name === null ? '—' : original.company_name}
        </span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      id: 'status',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-primary" />
          Status
        </span>
      ),
      cell: ({ row: { original } }) => {
        const config = original.is_active
          ? {
              label: 'Active',
              className: 'badge-phoenix-success',
              icon: 'check-circle'
            }
          : {
              label: 'Inactive',
              className: 'badge-phoenix-secondary',
              icon: 'slash'
            };

        return (
          <span className={`badge badge-phoenix fs-10 ${config.className}`}>
            <span className="badge-label">{config.label}</span>
            <FeatherIcon icon={config.icon} size={12} className="ms-1" />
          </span>
        );
      },
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },

    // {
    //   id: 'status',
    //   header: 'Status',
    //   cell: ({ row: { original } }) => (
    //     <span
    //       className={`badge ${
    //         original.is_active ? 'bg-success' : 'bg-secondary'
    //       }`}
    //     >
    //       {original.is_active ? 'Active' : 'Inactive'}
    //     </span>
    //   ),
    //   meta: {
    //     headerProps: {},
    //     cellProps: {}
    //   }
    // },
    {
      id: 'actions',
      header: () => (
        <div className="d-inline-flex align-items-center">
          <FontAwesomeIcon icon={faCog} className="me-2 text-primary" />
          Actions
        </div>
      ),
      cell: ({ row: { original } }) => (
        <RevealDropdownTrigger>
          <RevealDropdown>
            <ActionDropdownItems
              onView={() => onView(original)}
              onEdit={() => onEdit(original)}
              onRemove={() => onDelete(original)}
            />
          </RevealDropdown>
        </RevealDropdownTrigger>
      ),
      meta: {
        headerProps: { className: 'text-end pe-3' },
        cellProps: { className: 'text-end pe-3' }
      }
    }
  ];
}
